import Marker, {IconType, LocationType} from "@churchofjesuschrist/maps-google-marker";
import LabelMarker from "@churchofjesuschrist/maps-google-label-marker";
import ServiceOverlay from "@churchofjesuschrist/maps-google-service-overlay";
import {
	cloneOverlay,
	containsOverlay,
	getPolysWithEdges,
	hasIntersections,
	isLocationOnPixelEdge
} from "@churchofjesuschrist/maps-google-overlays";
import {separatePolygonPaths} from "@churchofjesuschrist/maps-google-math";

export default function MapDrawService($q, $document, $state, $timeout, DataConst, DataService, MapService, utils, google) {
	"ngInject";

	const colors = {
		select: "#43B1E3",
		draw: "#eb008b",
		alert: "#d02e2f",

		white: "#FFFFFF",

		translucent: 0.5
	};

	const zIndices = {
		select: 100,
		highlight: 1000,
		top: 20
	};

	const MapDrawService = {
		styles: {
			OVERLAY_SELECT: {
				padding: 2
			},
			OVERLAY_HIGHLIGHT: {
				color: colors.select,
				padding: 2
			},
			POLYLINE_DRAW: {
				strokeColor: colors.draw,
				strokeWeight: 4,
				strokeOpacity: 1,
				clickable: false,
				zIndex: zIndices.highlight + 1
			},
			POLYLINE_TRAIL: {
				strokeColor: colors.draw,
				strokeWeight: 4,
				strokeOpacity: 0.8,
				clickable: false,
				zIndex: zIndices.highlight + 2
			},
			POLYGON_HIGHLIGHT: {
				strokeColor: colors.select,
				strokeWeight: 5,
				strokeOpacity: 1,
				fillOpacity: 0,
				zIndex: zIndices.highlight,
				clickable: false
			},
			POLYGON_ALERT: {
				strokeColor: colors.alert,
				strokeWeight: 5,
				strokeOpacity: 1,
				fillColor: colors.alert,
				fillOpacity: colors.translucent,
				zIndex: zIndices.top,
				clickable: false
			},
			POLYGON_SELECT: {
				strokeColor: colors.select,
				strokeWeight: 5,
				strokeOpacity: 1,
				fillColor: colors.select,
				fillOpacity: colors.translucent,
				zIndex: zIndices.select,
				clickable: true
			},
			POLYGON_SELECT_CUSTOM: {
				strokeColor: colors.select,
				strokeWeight: 5,
				strokeOpacity: 1,
				fillColor: MapService.getColor("CUSTOM"),
				fillOpacity: colors.translucent,
				zIndex: zIndices.select,
				clickable: true
			},
			POLYGON_UNSELECT: {
				strokeColor: colors.select,
				strokeWeight: 5,
				strokeOpacity: 1,
				fillOpacity: 0,
				zIndex: zIndices.top,
				clickable: true
			},
			MARKER_VERTEX: {
				icon: {
					fillColor: colors.white,
					fillOpacity: 1,
					strokeColor: colors.draw,
					strokeWeight: 2,
					scale: 6
				},
				clickable: false,
				zIndex: zIndices.top
			},
			MARKER_SNAP: {
				icon: {
					fillColor: colors.white,
					fillOpacity: 1,
					strokeColor: colors.draw,
					strokeWeight: 2,
					scale: 8
				},
				clickable: false,
				zIndex: zIndices.top + 1
			},
			MARKER_SNAP_END: {
				icon: {
					fillColor: colors.draw,
					fillOpacity: 1,
					strokeColor: colors.white,
					strokeWeight: 2,
					scale: 10
				},
				clickable: false,
				zIndex: zIndices.top + 1
			},
			SNAP_WIDTH: 8 + 2   //MARKER_SNAP.icon.scale + MARKER_SNAP.icon.strokeWeight
		},

		method: {NORMAL: "normal", BOUNDARY: "boundary"},

		historyEvent: {TOGGLE: "toggle", VERTEX: "vertex", POLYGON: "polygon"},

		init: () => {
			MapService.loadMapsApi().then(() => {
				MapDrawService.styles.MARKER_VERTEX.icon.path = google.maps.SymbolPath.CIRCLE;
				MapDrawService.styles.MARKER_SNAP.icon.path = google.maps.SymbolPath.CIRCLE;
				MapDrawService.styles.MARKER_SNAP_END.icon.path = google.maps.SymbolPath.CIRCLE;
			});
		},

		drawOrgBoundary: (org, map, prioritize) => {
			let color = MapService.getColor(org.type);
			org.boundary.setOptions({
				strokeColor: color,
				strokeWeight: MapService.getWidth(org.type),
				strokeOpacity: 1,
				fillColor: color,
				fillOpacity: (prioritize ? colors.translucent : 0),
				zIndex: MapService.getZIndex(org.type) + (prioritize ? zIndices.top : 0),
				clickable: false,
				data: {org, prioritize}
			});
			MapDrawService.updateOverlayMap(org.boundary, map);
			return org.boundary;
		},
		drawOrgFacility: (org, map, prioritize) => {
			if (!org.marker) {
				org.marker = new Marker({
					icon:{
						type: IconType.PIN,
						layer: org.type,
					},
					position: org.position
				});
				org.marker.addListener("click", () => {
					DataService.loadBounds(org).then((org) => {
						MapService.centerMap(org, org.marker.getMap());
					});
				});

				org.marker.addListener("mouseover", () => {
					MapDrawService.drawOrgHighlighted(org);
				});
				org.marker.addListener("mouseout", () => {
					MapDrawService.drawOrgHighlighted();
				});
			}
			org.marker.setOptions({
				icon: {
					type: IconType.PIN,
					layer: org.type,
					color: org.highlight ? colors.select : null
				},
				zIndex: MapService.getZIndex(org.type) + (prioritize ? zIndices.top : 0) + (org.highlight ? zIndices.highlight : 0),
				data: {org, prioritize}
			});
			MapDrawService.updateOverlayMap(org.marker, map);

			return org.marker;
		},
		drawOrgLabel: (org, map, prioritize) => {
			let initialize = !org.label;
			if (initialize) {
				let target = org.marker || MapService.serializeIdentity(org);
				if (DataService.isBoundaryPending(org)) {
					if (org.boundary) {
						target = cloneOverlay(org.boundary);
					} else {
						console.warn("Org pending boundary missing: ", org);
					}
				}

				if (DataConst.FACILITY_UNIT_ORG_TYPES.indexOf(org.type) !== -1) {
					org.label = new LabelMarker({
						multiple: true,
						position: org.position?org.position:{},
						text: org.name,
						className: `map__label map__label--${org.type}`
					});
				}
				else {
					org.label = new LabelMarker({
						target,
						multiple: true,
						text: org.name,
						className: `map__label map__label--${org.type}`
					});
				}

				org.label.addListener("click", () => {
					let prevent = false,
						opts = DataService.getMapOpts();
					if (opts.onLabelClick) {
						prevent = opts.onLabelClick(org) === false;
					}
					if (!prevent) {
						let hasPage = DataConst.MISSION_ORG_TYPES.indexOf(org.type) !== -1;
						if (hasPage && !opts.labelsClean) {
							utils.goOrgState(org);
						} else {
							DataService.loadBounds(org).then((org) => {
								MapService.centerMap(org, org.label.getMap());
							});
						}
					}
				});

				org.label.addListener("mouseover", () => {
					MapDrawService.drawOrgHighlighted(org);
				});
				org.label.addListener("mouseout", () => {
					MapDrawService.drawOrgHighlighted();
				});
			}

			org.label.setOptions({
				zIndex: MapService.getZIndex(org.type) + (prioritize ? zIndices.top : 0),
				data: {org, prioritize}
			});

			let showStatus = !DataService.getMapOpts().labelsClean;
			MapDrawService.toggleLabelClass(org.label, "map__label--overlaps", showStatus && DataService.isStatusFlagged(org));
			MapDrawService.toggleLabelClass(org.label, "map__label--referrals", showStatus && DataService.isNonReferrals(org));

			MapDrawService.toggleLabelClass(org.label, "map__label--highlight", org.highlight);

			MapDrawService.updateOverlayMap(org.label, map);
			if (initialize) {
				// needed until GISAPI-624 is fixed
				setTimeout(() => {
					MapDrawService.updateOverlayMap(org.label, map);
				});
			}

			return org.label;
		},
		toggleLabelClass: (label, className, add) => {
			let classNames = label.getClassName() || "",
				classes = classNames.split(" "),
				index = classes.indexOf(className),
				exists = index !== -1;
			if (add && !exists) {
				classes.push(className);
			} else if (!add && exists) {
				classes.splice(index, 1);
			}
			classNames = classes.join(" ");
			label.setClassName(classNames);
			return classNames;
		},
		drawOrgSelected: (org, map, highlight) => {
			// use org boundary if it's there instead of overlay
			let overlay = null,
				style = highlight ? MapDrawService.styles.OVERLAY_HIGHLIGHT : MapDrawService.styles.OVERLAY_SELECT,
				zIndex = MapService.getZIndex(org.type) + (highlight ? zIndices.highlight : zIndices.select);
			if (org.boundary) {
				org.boundary.setOptions(angular.extend({}, MapDrawService.styles.POLYGON_HIGHLIGHT, {
					strokeColor: highlight ? style.color : MapService.getColor(org.type),
					strokeWeight: MapService.getWidth(org.type) + style.padding * 2,
					zIndex
				}));
				MapDrawService.updateOverlayMap(org.boundary, map);
				overlay = org.boundary;
			} else {
				overlay = new ServiceOverlay({
					ids: [MapService.getIdentity(org)],
					data: style,
					zIndex,
					map
				});
			}
			return overlay;
		},
		drawOrgHighlighted: (org) => {
			let selected = null,
				timer = null;

			MapDrawService.drawOrgHighlighted = (org) => {
				if (!selected || selected.org !== org) {
					if (selected) {
						$timeout.cancel(timer);

						// listing
						delete selected.org.highlight;

						// boundary
						MapDrawService.removeOverlays(selected.overlay);

						// marker
						if (selected.markerOpts) {
							MapDrawService.drawOrgFacility(selected.org, selected.markerOpts.map, selected.markerOpts.prioritize);
						}

						//label
						if (selected.labelOpts) {
							MapDrawService.drawOrgLabel(selected.org, selected.labelOpts.map, selected.labelOpts.prioritize);
						}

						selected = null;
						$timeout(angular.noop);
					}

					if (org) {
						let hasBoundary = DataService.hasBoundary(org),
							isFacility = DataService.isFacility(org);
						if (hasBoundary || isFacility) {
							selected = {org};
							timer = $timeout(() => {
								let map = DataService.instantMap(),
									hasBoundary = DataService.hasBoundary(selected.org),
									isFacility = DataService.isFacility(selected.org);

								// listing
								selected.org.highlight = true;

								// boundary or marker
								if (hasBoundary) {
									let boundary = selected.org.boundary ? cloneOverlay(selected.org.boundary) : null,
										tempOrg = angular.extend({}, selected.org, {boundary});
									selected.overlay = MapDrawService.drawOrgSelected(tempOrg, map, true);
								} else if (isFacility) {
									selected.markerOpts = {prioritize: false};
									if (selected.org.marker && selected.org.marker.getMap()) {
										angular.extend(selected.markerOpts, selected.org.marker.data, {
											map: org.marker.getMap()
										});
									}
									MapDrawService.drawOrgFacility(selected.org, map, selected.markerOpts.prioritize);
								}

								// label
								if (hasBoundary || isFacility) {
									selected.labelOpts = {prioritize: false};
									if (selected.org.label && selected.org.label.getMap()) {
										angular.extend(selected.labelOpts, selected.org.label.data, {
											map: org.label.getMap()
										});
									}
									MapDrawService.drawOrgLabel(selected.org, map, selected.labelOpts.prioritize);
								}
							}, 250);
						}
					}
				}
			};
			return MapDrawService.drawOrgHighlighted(org);
		},
		updateOverlayMap: (overlay, map) => {
			if (overlay && overlay.getMap() !== map) {
				overlay.setMap(map);
			}
		},
		removeOverlays: (overlays) => {
			if (overlays) {
				overlays = utils.toArray(overlays);
				overlays.forEach((overlay) => {
					if (overlay) {
						overlay.setMap(null);
					}
				});
			}
		},

		createDrawing: (map) => {
			let drawing = {
				marker: new google.maps.Marker(angular.extend({map}, MapDrawService.styles.MARKER_SNAP)),
				line: new google.maps.Polyline(angular.extend({map}, MapDrawService.styles.POLYLINE_DRAW)),
				vertices: []
			};
			drawing.clearMouseTrail = () => {
				MapDrawService.clearMouseTrail(drawing);
			};
			$document.on("mouseover", drawing.clearMouseTrail);
			return drawing;
		},
		removeDrawing: (drawing) => {
			if (drawing) {
				MapDrawService.removeOverlays([drawing.marker, drawing.line]);
				MapDrawService.resetMouseTrail(drawing);
				$document.off("mouseover", drawing.clearMouseTrail);
				drawing.vertices.forEach((vertex) => {
					MapDrawService.removeOverlays(vertex.marker);
				});
			}
		},
		restoreDrawing: (path, vertices, map) => {
			let drawing = MapDrawService.createDrawing(map);
			drawing.line.setPath(path);
			drawing.vertices = vertices;
			drawing.vertices.forEach((vertex) => {
				vertex.marker.setMap(map);
			});

			if (drawing.vertices.length) {
				drawing.start = drawing.vertices[0].position;
			}

			let currentVertex = drawing.vertices[drawing.vertices.length - 1];
			if (drawing.vertices.length >= 3 && drawing.start.equals(currentVertex.position)) {
				drawing.complete = true;
			}

			return drawing;
		},
		removeLastLineSegment: (drawing, layer, map) => {
			MapDrawService.removeLastVertex(drawing);

			let previousVertex = drawing.vertices[drawing.vertices.length - 1];
			layer.setExclusions(previousVertex && previousVertex.snapped ? drawing.line : null);

			MapDrawService.resetMouseTrail(drawing, map);
		},
		addLineSegment: (position, snapped, drawing, layer, map) => {
			drawing.adding = true;
			let d = $q.defer();
			MapDrawService.getLineSegment(position, snapped, drawing, layer, true).then((lineSegment) => {
				if (!lineSegment.error) {
					MapDrawService.addVertex(lineSegment, drawing, layer, map);
					layer.setExclusions(snapped && !drawing.complete ? drawing.line : null);
					MapDrawService.resetMouseTrail(drawing, map);
				}

				delete drawing.adding;
				d.resolve(lineSegment);
			});
			return d.promise;
		},
		getLineSegment: (position, snapped, drawing, layer, validate) => {
			let d = $q.defer();
			setTimeout(() => {
				let handled = false;

				// 1. follow polygons
				if (snapped) {
					let previousVertex = drawing.vertices[drawing.vertices.length - 1];
					if (previousVertex && previousVertex.snapped && MapDrawService.isSnapped(position, layer)) {
						layer.getShortestLineSegment(previousVertex.position, position).then((segment) => {
							d.resolve(
								segment ?
									{method: MapDrawService.method.BOUNDARY, path: segment.getPath().getArray()} :
									{method: MapDrawService.method.NORMAL, path: [position]}
							);
						});

						handled = true;
					}
				}

				// 2. go direct
				if (!handled) {
					d.resolve({method: MapDrawService.method.NORMAL, path: [position]});
				}
			});
			return d.promise.then((lineSegment) => {
				if (validate) {
					let error = MapDrawService.validateLineSegment(lineSegment, snapped, drawing, layer);
					if (error) {
						lineSegment.error = error;
					}
				}
				return lineSegment;
			});
		},
		validateLineSegment: (lineSegment, snapped, drawing, layer) => {
			// validate vertex, if invalid undo
			let invalid = null,
				position = lineSegment.path[lineSegment.path.length - 1],
				border = layer.get("border");

			// check if outside layer
			if (!snapped && !google.maps.geometry.poly.containsLocation(position, border)) {
				invalid = "outside_border";
			}
			if (!invalid && drawing.vertices.length) {
				let previousVertex = drawing.vertices[drawing.vertices.length - 1],
					newLineSegment = new google.maps.Polyline({path: [previousVertex.position].concat(lineSegment.path)}),
					followingBoundary = lineSegment.method === MapDrawService.method.BOUNDARY;

				// check if line crosses outside then inside of layer
				if (!followingBoundary && !containsOverlay(newLineSegment, border)) {
					invalid = "outside_border";
				}

				//check if line crosses itself
				if (!invalid) {
					let exclude = [newLineSegment.getPath().getAt(0)],
						completable = drawing.start.equals(position);
					if (completable) {
						exclude.push(position);
					}

					let intersects = hasIntersections(drawing.line, newLineSegment, exclude);
					if (intersects) {
						invalid = "crosses_itself";
					}
				}
			}

			return invalid;
		},
		addVertex: (lineSegment, drawing, layer, map) => {
			// adjust drawing line
			let newPath = drawing.line.getPath().getArray().concat(lineSegment.path);
			drawing.line.setPath(newPath);

			// add start
			let index = drawing.line.getPath().getLength() - 1,
				position = newPath[newPath.length - 1];
			if (!drawing.vertices.length) {
				drawing.start = position;
			}

			// add vertex marker
			let marker = new google.maps.Marker(angular.extend({position, map}, MapDrawService.styles.MARKER_VERTEX)),
				snapped = MapDrawService.isSnapped(position, layer);
			drawing.vertices.push({index, position, marker, snapped});

			// check for completion
			let currentVertex = drawing.vertices[drawing.vertices.length - 1];
			if (drawing.vertices.length >= 3 && drawing.start.equals(currentVertex.position)) {
				drawing.complete = true;
			}

			return currentVertex;
		},
		removeLastVertex: (drawing) => {
			if (drawing.vertices.length) {
				let vertex = drawing.vertices.pop(),
					previousVertex = drawing.vertices[drawing.vertices.length - 1];

				// adjust drawing line
				let previousPath = drawing.line.getPath().getArray().slice(0, previousVertex ? previousVertex.index + 1 : 0);
				drawing.line.setPath(previousPath);

				// remove start
				if (!drawing.vertices.length) {
					delete drawing.start;
				}

				// remove vertex marker
				vertex.marker.setMap(null);

				// remove completion
				drawing.complete = false;

				return vertex;
			} else {
				return null;
			}
		},
		getSnapPosition: (position, drawing, layer, map) => {
			let snapPosition = null;

			// 1. snap to start
			if (drawing.vertices.length >= 3 && isLocationOnPixelEdge(position, drawing.start, MapDrawService.styles.SNAP_WIDTH, map)) {
				snapPosition = drawing.start;
			}

			// 2. snap to intersections
			if (!snapPosition) {
				let intersections = layer.getIntersectionsPositions();
				if (intersections) {
					let snapIntersectionWidth = MapDrawService.styles.SNAP_WIDTH * 2 + 2;
					for (let i = 0; i < intersections.length; i++) {
						let intersection = intersections[i];
						if (isLocationOnPixelEdge(position, intersection, snapIntersectionWidth, map)) {
							snapPosition = intersection;
							break;
						}
					}
				}
			}

			// 3. snap to polygon line
			if (!snapPosition) {
				let snap = layer.getClosest(position);

				if (isLocationOnPixelEdge(position, snap.overlay, MapDrawService.styles.SNAP_WIDTH, map)) {
					snapPosition = snap.position;
				}
			}

			return snapPosition;
		},
		isSnapped: (position, layer) => {
			let matches = getPolysWithEdges(layer.getPolygons(), position);
			return matches.length > 0;
		},
		refreshMouseTrail: (position, drawing, layer, map) => {
			if (drawing && !drawing.complete && !drawing.adding) {
				drawing.mouse = position;

				let snap = MapDrawService.getSnapPosition(position, drawing, layer, map),
					snapped = snap !== null;
				drawing.marker.setPosition(snap);
				if (snap === drawing.start) {
					drawing.marker.setOptions(MapDrawService.styles.MARKER_SNAP_END);
				} else {
					drawing.marker.setOptions(MapDrawService.styles.MARKER_SNAP);
				}

				if (drawing.trail) {
					setTimeout(() => {
						if (drawing.trail && drawing.mouse === position) {
							MapDrawService.getLineSegment(snap || position, snapped, drawing, layer).then((lineSegment) => {
								if (drawing.trail && drawing.mouse === position) {
									let end = drawing.line.getPath().getAt(drawing.line.getPath().getLength() - 1);
									drawing.trail.setPath([end].concat(lineSegment.path));
								}
							});
						}
					});
				}
			}
		},
		resetMouseTrail: function (drawing, map) {
			if (!drawing.complete && drawing.line && drawing.line.getPath().getLength()) {
				if (drawing.trail) {
					drawing.trail.setPath([]);
				} else {
					drawing.trail = new google.maps.Polyline(angular.extend({map}, MapDrawService.styles.POLYLINE_TRAIL));
				}
			} else {
				if (drawing.trail) {
					drawing.trail.setMap(null);
					delete drawing.trail;
				}
			}
			if (drawing.marker) {
				drawing.marker.setPosition(null);
			}
		},
		clearMouseTrail: (drawing) => {
			if (drawing) {
				if (drawing.trail) {
					drawing.trail.setPath([]);
				}
				if (drawing.marker) {
					drawing.marker.setPosition(null);
				}
			}
		},
		adjustPolygonsLayer: (polygons, adjustment, fixedPolygons) => (
			MapService.generateLayer(fixedPolygons, adjustment).then((adjustments) => (
				MapDrawService.adjustPolygons(polygons, adjustments)
			))
		),
		adjustPolygons: (polygons, adjustment) => {
			let current = polygons.map((polygon, index) => (
				{
					id: index + 1,
					boundary: polygon
				}
			));

			return MapService.adjust(current, adjustment).then((adjustedPolygons) => {
				let results = [...polygons],
					adjustments = [],
					replaced = [],
					created = [];

				adjustedPolygons.forEach((adjustedPolygon) => {
					let boundaryPolygons = MapDrawService.splitPolygonsPaths(adjustedPolygon.boundary || adjustedPolygon);

					if (adjustedPolygon.id) {
						let oldIndex = adjustedPolygon.id - 1,
							polygon = polygons[oldIndex],
							data = polygon.get("data");

						replaced.push(polygon);
						utils.removeItem(polygon, results);
						boundaryPolygons.forEach((boundary) => {
							boundary.set("data", data);
						});
					} else {
						created.push(...boundaryPolygons);
					}

					adjustments.push(...boundaryPolygons);
					results.push(...boundaryPolygons);
				});

				return {polygons: results, adjustments, created, replaced};
			});
		},
		splitPolygonsPaths: (polygons, lines) => {
			let results = [];
			if (polygons) {
				polygons = utils.toArray(polygons);
				polygons.forEach((polygon) => {
					let wholePaths = separatePolygonPaths(polygon);
					wholePaths.forEach((paths) => {
						if (lines) {
							paths.forEach((path) => {
								results.push(new google.maps.Polyline({path}));
							});
						} else {
							results.push(new google.maps.Polygon({paths}));
						}
					});
				});
			}
			return results;
		},
		getCustomSelectablePolygons: (custom) => {
			let types = ["AREA", "CUSTOM"];
			return DataService.getOrgsByTypes(types).then((involvedOrgs) => {
				involvedOrgs = involvedOrgs.filter(DataService.hasBoundary);

				let promises = involvedOrgs.map(DataService.loadBoundary);
				return utils.allPromisesCompleted(promises).then(() => (
					DataService.loadMissionExtendedBoundary().then((mission) => {
						let border = utils.getOrgBoundary(mission),
							fixedBoundaries = involvedOrgs.filter(utils.getOrgBoundary).map(utils.getOrgBoundary);
						if (mission.boundary) {
							fixedBoundaries.push(mission.boundary);
						}

						return MapService.generateLayer(fixedBoundaries, border).then((polygons) => {
							let promises = polygons.map((polygon) => (
								MapDrawService.createSelectablePolygon(polygon).then((polygon) => {
									if (custom) {
										// only toggle those that are in the current boundary
										let metadata = polygon.get("data"),
											selected = google.maps.geometry.poly.containsLocation(metadata.centroid, custom.boundary);
										MapDrawService.selectCustomPolygon(polygon, selected);
									}
									return polygon;
								})
							));
							return $q.all(promises).then(() => (
								{selectable: polygons, border, involvedOrgs, fixedBoundaries}
							));
						});
					})
				));
			});
		},
		containsPartialShape: (entries) => {
			let shapes = [],
				polygons = [];
			let indices = entries.map((entry) => {
				let shapeIndex = shapes.indexOf(entry.shape);
				if (shapeIndex === -1) {
					shapes.push(entry.shape);
					shapeIndex = shapes.length - 1;
				}
				let polygonIndex = polygons.indexOf(entry.polygon);
				if (polygonIndex === -1) {
					polygons.push(entry.polygon);
					polygonIndex = polygons.length - 1;
				}

				return {entry, shapeIndex, polygonIndex};
			});
			return MapService.shapesRelate(shapes, polygons).then((relations) => {
				let results = [];
				relations.forEach((relation) => {
					indices
						.filter((data) => (data.shapeIndex === relation.geometry1Index && data.polygonIndex === relation.geometry2Index))
						.forEach((data) => {
							results.push(data.entry.result);
						});
				});
				return results;
			});
		},
		getSelectablePolygons: (area, additionalFixedBoundaries) => (
			DataService.getOverlappingAreas(area).then((overlappingAreas) => {
				let assigned = DataService.getAssignedBoundaryOrgs(area),
					assignedParentChildren = DataService.getAssignedBoundaryUnits(area),
					involvedOrgs = [area].concat(assignedParentChildren).concat(overlappingAreas),
					promises = involvedOrgs.map(DataService.loadDetails);
				promises.push(...involvedOrgs.map(DataService.loadBoundary));

				// get other units added for parent/child coverage
				let otherUnits = [];
				overlappingAreas.forEach((overlappingArea) => {
					if (overlappingArea.assigned) {
						DataService.getAssignedBoundaryOrgs(overlappingArea).forEach((unit) => {
							if (!utils.containsOrg(assignedParentChildren, unit)) {
								otherUnits.push(unit);
							}
						});
					}
				});
				otherUnits = utils.uniqueOrgs(otherUnits);
				promises.push(...otherUnits.map(DataService.loadBoundary));

				return $q.all(promises).then(() => (
					DataService.mergeOrgs(assigned).then((border) => (
						DataService.loadMissionBoundary().then((mission) => {
							let otherUnitEntries = otherUnits.map((unit) => ({
								shape: unit.boundary,
								polygon: border,
								result: unit
							}));
							return MapDrawService.containsPartialShape(otherUnitEntries).then((involvedUnits) => {
								involvedOrgs.push(...involvedUnits);

								let fixedBoundaries = involvedOrgs.map(utils.getOrgBoundary),
									overlapBorder = mission.boundary;
								if (overlapBorder) {
									fixedBoundaries.push(overlapBorder);
								}
								if (additionalFixedBoundaries) {
									fixedBoundaries.push(...additionalFixedBoundaries);
								}
								return MapService.generateLayer(fixedBoundaries, border).then((polygons) => {
									let promises = polygons.map((polygon) => (
										MapDrawService.createSelectablePolygon(polygon, overlappingAreas).then((polygon) => {
											// only toggle those that are in the current boundary
											let metadata = polygon.get("data"),
												selected = google.maps.geometry.poly.containsLocation(metadata.centroid, area.boundary);
											MapDrawService.selectPolygon(polygon, selected);
											return polygon;
										})
									));
									return $q.all(promises).then(() => (
										{
											selectable: polygons,
											border,
											involvedOrgs,
											overlappingAreas,
											fixedBoundaries,
											overlapBorder
										}
									));
								});
							});
						})
					))
				));
			})
		),
		createSelectablePolygon: (polygon, overlappingAreas) => {
			let metadata = {
				selected: false,
				centroid: null,
				overlaps: null
			};
			polygon.setOptions(angular.extend({data: metadata}, MapDrawService.styles.POLYGON_UNSELECT));

			return MapService.centroid(polygon).then((centroid) => {
				metadata.centroid = centroid;
				if (metadata.centroid && overlappingAreas) {
					let isOverlap = false;
					for (let i = 0; i < overlappingAreas.length; i++) {
						let area = overlappingAreas[i];
						if (google.maps.geometry.poly.containsLocation(metadata.centroid, area.boundary)) {
							isOverlap = true;
							break;
						}
					}
					metadata.overlaps = isOverlap;
				}

				return polygon;
			});
		},
		selectPolygon: (polygon, select) => {
			let metadata = polygon.get("data");
			metadata.selected = select;
			polygon.setOptions(select ? MapDrawService.styles.POLYGON_SELECT : MapDrawService.styles.POLYGON_UNSELECT);
		},
		selectCustomPolygon: (polygon, select) => {
			let metadata = polygon.get("data");
			metadata.selected = select;
			polygon.setOptions(select ? MapDrawService.styles.POLYGON_SELECT_CUSTOM : MapDrawService.styles.POLYGON_UNSELECT);
		},
		isPolygonSelected: (polygon) => {
			let metadata = polygon.get("data");
			return metadata.selected;
		},
		createOverlappingPolygons: (selectablePolygons, map) => {
			let overlaps = [];
			selectablePolygons.forEach((polygon) => {
				let metadata = polygon.get("data");
				if (metadata.overlaps && metadata.selected) {
					let overlap = cloneOverlay(polygon);
					overlap.setOptions(angular.extend({map}, MapDrawService.styles.POLYGON_ALERT));
					overlaps.push(overlap);
				}
			});
			return overlaps;
		},
		compileSegments: (area, selectablePolygons, overlappingAreas, overlapBorder) => (
			selectablePolygons.map((polygon, index) => {
				let metadata = polygon.get("data"),
					segment = {
						index,
						polygon,
						areas: [],
						units: []
					};
				if (metadata.selected) {
					segment.areas.push(area.id);
				}
				if (metadata.centroid) {
					const addUnits = (segment, area, centroid) => {
						if (area.assigned) {
							DataService.getAssignedBoundaryUnits(area).forEach((unit) => {
								if (unit.boundary && segment.units.indexOf(unit.id) === -1 && google.maps.geometry.poly.containsLocation(centroid, unit.boundary)) {
									segment.units.push(unit.id);
								}
							});
						}
					};

					addUnits(segment, area, metadata.centroid);

					if (overlappingAreas) {
						overlappingAreas.forEach((overlappingArea) => {
							addUnits(segment, overlappingArea, metadata.centroid);

							if (google.maps.geometry.poly.containsLocation(metadata.centroid, overlappingArea.boundary)) {
								segment.areas.push(overlappingArea.id);
							}
						});
					}

					if (overlapBorder) {
						segment.outsideOverlapBorder = !google.maps.geometry.poly.containsLocation(metadata.centroid, overlapBorder);
					}
				}
				return segment;
			})
		),
		isOutsideMission: (area) => (
			DataService.loadMissionBoundary().then((mission) => (
				DataService.loadBoundary(area).then((area) => (
					MapService.generateLayer([area.boundary, mission.boundary]).then((polygons) => {
						let outside = false;

						let promises = polygons.map((polygon) => (
							MapService.centroid(polygon).then((centroid) => {
								if (!outside) {
									outside = !google.maps.geometry.poly.containsLocation(centroid, mission.boundary);
								}
							})
						));

						return $q.all(promises).then(() => (
							outside
						));
					})
				))
			))
		),
		compileUnitBoundaries: (area, segments) => {
			let promises = [];
			// merge single boundary for each unit, only if that unit is partial
			if (area.assigned) {
				DataService.getAssignedBoundaryOrgs(area).forEach((child) => {
					let unit = {id: child.id, name: child.name, boundary: null},
						unitId = unit.id,
						boundaries = [],
						partial = false,
						d = $q.defer();

					segments
						.filter((segment) => (segment.units.indexOf(unitId) !== -1))
						.forEach((segment) => {
							let selected = segment.areas.indexOf(area.id) !== -1;
							if (selected) {
								boundaries.push(segment.polygon);
							} else {
								partial = true;
							}
						});

					if (partial) {
						MapService.merge(boundaries).then((boundary) => {
							unit.boundary = boundary;
							d.resolve(unit);
						});
					} else {
						d.resolve(unit);
					}

					promises.push(d.promise);
				});
			}

			return $q.all(promises).then((units) => (
				units.filter((unit) => (unit.boundary))
			));
		},

		createHistoryEntry: (type, data, selectable) => {
			let entry = {type};
			switch (entry.type) {
				case MapDrawService.historyEvent.TOGGLE:
					entry.index = selectable.indexOf(data);
					break;
				case MapDrawService.historyEvent.VERTEX:
					entry.lineSegment = data;
					break;
				case MapDrawService.historyEvent.POLYGON:
					entry.state = data;
					break;
			}
			return entry;
		},
		getCurrentState: (selectable, drawing) => {
			let selectableState = selectable.map((polygon, index) => (
					{polygon, index, selected: MapDrawService.isPolygonSelected(polygon)}
				)),
				drawingState = drawing ? {
					vertices: utils.cloneArray(drawing.vertices),
					path: drawing.line.getPath().getArray()
				} : null;
			return {selectable: selectableState, drawing: drawingState};
		}

	};

	MapDrawService.init();

	return MapDrawService;
}
